export function getGraphColor(name) {
  // Return the color for the given graph
  switch (name) {
    case "FACEBOOK":
      return "#1877F2";
    case "GOOGLE":
      return "#FF4040";
    case "HELLOPETER":
      return "#29357f";
    case "FRESHA":
      return "#6950f3";
    case "TRIPADVISOR":
      return "#34E0A1";
    case "INSTAGRAM":
      return "#E4405F";
    default:
      return "#000000";
  }
}

// Utility function to map periods to start and end dates
export function mapPeriodToDateRange(period, custom = {}) {
  const currentDate = new Date(); // Get current date
  let startDate = new Date();
  let endDate = new Date();

  // Set the time for startDate and endDate based on the selected period
  switch (period) {
    case "Today":
      // Set start date to the beginning of the current day
      startDate.setHours(0, 0, 0, 0);
      break;
    case "Last 7 Days":
      // Set start date to 7 days ago and end date to today
      startDate.setDate(currentDate.getDate() - 6); // 7 days ago
      break;
    case "Last 14 Days":
      // Set start date to 7 days ago and end date to today
      startDate.setDate(currentDate.getDate() - 13); // 7 days ago
      break;
    case "Last 30 Days":
      // Set start date to 30 days ago and end date to today
      startDate.setDate(currentDate.getDate() - 29); // 30 days ago
      break;
    case "Last 90 Days":
      // Set start date to 30 days ago and end date to today
      startDate.setDate(currentDate.getDate() - 89); // 30 days ago
      break;
    case "Last 365 Days":
      // Set start date to 365 days ago and end date to today
      startDate.setDate(currentDate.getDate() - 364);
      break;
    case "Custom": {
      // Use the explicit range stored on the main store; fall back to all-time
      const customStart = custom.customStart
        ? new Date(custom.customStart)
        : new Date(0);
      const customEnd = custom.customEnd
        ? new Date(custom.customEnd)
        : currentDate;
      return { startDate: customStart, endDate: customEnd };
    }
    // Add more cases for other periods as needed
    default:
      // For 'All Time' or any other default period, set start date to a past date (e.g., Jan 1, 1970)
      startDate = new Date(0); // Jan 1, 1970
      break;
  }

  // Set the end date to the current date
  endDate = currentDate;

  // Return an object containing start and end dates
  return { startDate, endDate };
}

// Human-readable label for a custom date range, e.g. "1 Jan – 26 Jun 2026".
// Omits the start year when both ends share it; end always shows the year.
export function formatDateRange(start, end) {
  if (!start) return "Custom";
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const sameYear = s.getFullYear() === e.getFullYear();
  const startOpts = sameYear
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" };
  const endOpts = { day: "numeric", month: "short", year: "numeric" };
  return `${s.toLocaleDateString("en-GB", startOpts)} – ${e.toLocaleDateString("en-GB", endOpts)}`;
}

// "en-GB" is intentional: it forces day-before-month order (e.g. "12 Jun") to
// match the SA locale convention used throughout the app. Do not change to en-US.
// Short x-axis label for trend charts — no year (e.g. "Jun" / "12 Jun")
export function formatTrendAxis(value, aggregation) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return aggregation === "Monthly"
    ? date.toLocaleString("en-GB", { month: "short" })
    : date.toLocaleString("en-GB", { day: "numeric", month: "short" });
}

// Tooltip label for trend charts — always includes year (e.g. "Jun 2026" / "12 Jun 2026")
export function formatTrendTip(value, aggregation) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return aggregation === "Monthly"
    ? date.toLocaleString("en-GB", { month: "short", year: "numeric" })
    : date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

export function formatTime(totalMinutes) {
  if (totalMinutes < 0) {
    return { isValid: false };
  }

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return {
      isValid: true,
      value: days,
      suffix: ` day${days !== 1 ? "s" : ""}`,
    };
  } else if (hours > 0) {
    return {
      isValid: true,
      value: hours,
      suffix: ` hour${hours !== 1 ? "s" : ""}`,
    };
  } else {
    return {
      isValid: true,
      value: minutes,
      suffix: ` min${minutes !== 1 ? "s" : ""}`,
    };
  }
}

// "1 mention" / "2 mentions" — a thousands-formatted count followed by the noun,
// pluralised on the raw (pre-format) value so exactly 1 stays singular.
export function countLabel(count, singular, plural = `${singular}s`) {
  const n = Number(count) || 0;
  return `${n.toLocaleString()} ${n === 1 ? singular : plural}`;
}
